'use client';

import { Button, ActionIcon, Tooltip } from '@mantine/core';
import { IconBrandLinkedin, IconShare } from '@tabler/icons-react';

interface LinkedInShareButtonProps {
  /** The URL to share */
  url: string;
  /** The text to include in the LinkedIn post */
  text: string;
  /** Button variant */
  variant?: 'filled' | 'light' | 'outline' | 'subtle';
  /** Button size */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Full width button */
  fullWidth?: boolean;
  /** Additional class name */
  className?: string;
  /** Show as compact icon-only button */
  compact?: boolean;
}

/**
 * A button component for sharing content on LinkedIn
 */
export function LinkedInShareButton({
  url,
  text,
  variant = 'light',
  size = 'sm',
  fullWidth = false,
  className,
  compact = false,
}: LinkedInShareButtonProps) {
  const handleShare = () => {
    // LinkedIn share URL format
    // Using the URL parameter for sharing. The text parameter can be included as the title
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    
    // Open LinkedIn share dialog in a new window
    window.open(
      linkedInUrl,
      'linkedin-share-dialog',
      'width=626,height=436'
    );
  };

  if (compact) {
    return (
      <Tooltip label="Share on LinkedIn" position="bottom" withArrow>
        <Button
          onClick={handleShare}
          leftSection={<IconShare size={14} />}
          variant={variant}
          color="blue"
          size={size}
          className={className}
          style={{
            borderColor: '#0077B5',
            color: variant === 'filled' ? 'white' : '#0077B5',
            backgroundColor: variant === 'filled' ? '#0077B5' : undefined,
            paddingLeft: size === 'xs' ? 8 : 12,
            paddingRight: size === 'xs' ? 10 : 14,
          }}
        >
          Share
        </Button>
      </Tooltip>
    );
  }

  return (
    <Button
      onClick={handleShare}
      leftSection={<IconBrandLinkedin size={16} />}
      variant={variant}
      color="blue"
      size={size}
      fullWidth={fullWidth}
      className={className}
      style={{
        borderColor: '#0077B5',
        color: variant === 'filled' ? 'white' : '#0077B5',
        backgroundColor: variant === 'filled' ? '#0077B5' : undefined,
      }}
    >
      Share on LinkedIn
    </Button>
  );
}
